import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { SpecificationContentType } from '../../enums';
import { SpecificationEmbodimentOutlineService } from '../specification-embodiment-outline-service';
import { SpecificationGenParagraphApi } from '../../apis/gen-specification-paragraph';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { AbstractBaseSection } from './abstract-base-section';
import { StreamStatus } from '@/services/sse-ps-http/enums';
import type { SpecificationSection } from '@/domains/specification/services/specification-section';
import { EmbodimentItemSection } from '@/domains/specification/services/sections/embodiment-item-section';
import type { EmbodimentOutline } from '@/domains/specification/apis/create-embodiment-outline-api';
import { EmbodimentConclusionSection } from '@/domains/specification/services/sections/embodiment-conclusion-section';

@injectable()
export class EmbodimentOutlineSection extends AbstractBaseSection {
    readonly contentType = SpecificationContentType.EmbodimentOutline;

    constructor(
        @inject(CurrentTaskService)
        readonly currentTaskService: CurrentTaskService,
        @inject(SpecificationGenParagraphApi)
        readonly genParagraphApi: SpecificationGenParagraphApi,
        @inject(SpecificationEmbodimentOutlineService)
        private readonly _outlineService: SpecificationEmbodimentOutlineService,
        @inject(EmbodimentItemSection)
        private _itemSection: EmbodimentItemSection,
        @inject(EmbodimentConclusionSection)
        private _conclusionSection: EmbodimentConclusionSection,
    ) {
        super();
    }

    private _currentSection: SpecificationSection | undefined;

    async beforeGenerate(regenerate?: boolean) {
        if (!regenerate) {
            return await this._outlineService.pass();
        }
        this._outlineService.enter();
        await this._outlineService.creatingDeferred?.promise;
    }

    async generate(regenerate?: boolean) {
        let currentContent = this.updateContent({
            type: this.contentType,
            status: StreamStatus.PROCESSING,
            content: this._outlineService.model.modelData.summary?.text || '',
            children: [],
        });

        const outlines = this._outlineService.model.modelData.outlines || [];

        const createTask = <P = any>(sectionService: SpecificationSection) => async (params?: P) => {
            const idx = this.currentContent!.children!.length;
            this._currentSection = sectionService;
            // @ts-ignore
            params && await sectionService.prepare(params);
            const provider = sectionService.execute(regenerate);

            try {
                await provider.subscribe((paragraph) => {
                    const children = currentContent!.children || [];
                    children[idx] = paragraph;
                    currentContent = this.updateContent({
                        ...currentContent,
                        children,
                        status: StreamStatus.PROCESSING,
                    });
                }).promise;
            } catch (err: any) {
                currentContent = this.updateContent({
                    ...currentContent,
                    status: StreamStatus.FAILED,
                });
                throw err;
            }
        };

        const doItemTask = createTask<EmbodimentOutline>(this._itemSection);

        for (const outline of outlines) {
            await doItemTask(outline);
        }

        const doConclusionTask = createTask(this._conclusionSection);

        await doConclusionTask();

        this.updateContent({
            ...this.currentContent!,
            status: StreamStatus.FINISH,
        });
    }

    stop() {
        if (this._currentSection) {
            this._currentSection.stop();
            this._currentSection = undefined;
        }
        this._outlineService.abort();
    }
}
